import moment from "moment";
import React from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import axiosInstance from "../../../utils/axiosInstance";
import { getUrlForBookings } from "../../../utils/confighelpers";
import { Avatar, Badge } from "../../classes/members/memberUtils";
import { ReceiptMenu, useReceiptActions } from "../../../components/receipts/ReceiptActions";
import { userState } from "../../../redux/reducers/userReducer";
import { axisTick, bucketLabels, ChartShell, clubSinceDate, COLORS, EmptyLine, euro, euroAxis, fetchBuckets, findRange, LinkButton, ListSkeleton, Panel, PAST_RANGES, Picker, revenueScopeText, startDateFor, TooltipBox } from "./shared";

/* ------------------------------------------------------------------ */
/* Revenue chart                                                       */
/* ------------------------------------------------------------------ */

export function RevenueChartCard() {
  const [rangeKey, setRangeKey] = React.useState("l6m");
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const reqRef = React.useRef(0);
  const range = findRange(PAST_RANGES, rangeKey);
  const since = clubSinceDate(useSelector(userState).userData);

  const load = React.useCallback(async () => {
    const id = ++reqRef.current;
    setLoading(true);
    setError("");
    try {
      const buckets = await fetchBuckets("allRevenueStats", { interval: range.interval, size: range.size, startDate: startDateFor(range, false) });
      if (id !== reqRef.current) return;
      setRows(buckets.map((b) => ({ ...bucketLabels(b, range.interval, range.size), value: parseFloat(b.count) || 0 })));
    } catch (e) {
      if (id === reqRef.current) setError(e.message || "Couldn't load revenue.");
    } finally {
      if (id === reqRef.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeKey]);
  React.useEffect(() => {
    load();
  }, [load]);

  const sum = rows.reduce((a, r) => a + r.value, 0);
  return (
    <Panel
      title="Revenue"
      subtitle={
        <span className="flex flex-wrap items-baseline gap-x-2">
          <span className="text-2xl font-black text-gray-900">{loading ? "–" : euro(sum)}</span>
          <span className="font-bold text-gray-600">{loading ? "" : `paid online in the ${range.title.toLowerCase()}`}</span>
        </span>
      }
      action={<Picker label="Revenue period" value={rangeKey} groups={PAST_RANGES} onChange={setRangeKey} />}
    >
      <ChartShell loading={loading} error={error} onRetry={load} empty={!loading && sum === 0} emptyText={`No payments in the ${range.title.toLowerCase()}`} height={260}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows} margin={{ top: 8, right: 4, left: -8, bottom: 0 }} barCategoryGap={range.size > 15 ? "18%" : "28%"}>
            <CartesianGrid vertical={false} stroke="#F1EEF2" />
            <XAxis dataKey="short" tick={axisTick} tickLine={false} axisLine={{ stroke: "#E3DCE6" }} interval="preserveStartEnd" minTickGap={8} />
            <YAxis allowDecimals={false} tick={axisTick} tickLine={false} axisLine={false} width={52} tickFormatter={euroAxis} />
            <Tooltip cursor={{ fill: "#F7F4F8" }} content={<TooltipBox rows={(d) => [{ label: "Revenue", value: euro(d.value), color: COLORS.revenue }]} />} />
            <Bar dataKey="value" fill={COLORS.revenue} radius={[4, 4, 0, 0]} maxBarSize={44} isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      </ChartShell>
      <div className="mt-4 flex items-start gap-2.5 rounded-2xl border border-[#E6DDF0] bg-[#FAF7FC] px-4 py-3 text-[13px] leading-relaxed text-gray-700">
        <svg viewBox="0 0 20 20" fill="currentColor" className="mt-0.5 h-4 w-4 shrink-0 text-indigo-bz" aria-hidden="true">
          <path fillRule="evenodd" d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-7-4a1 1 0 1 1-2 0 1 1 0 0 1 2 0ZM9 9a.75.75 0 0 0 0 1.5h.253a.25.25 0 0 1 .244.304l-.459 2.066A1.75 1.75 0 0 0 10.747 15H11a.75.75 0 0 0 0-1.5h-.253a.25.25 0 0 1-.244-.304l.459-2.066A1.75 1.75 0 0 0 9.253 9H9Z" clipRule="evenodd" />
        </svg>
        <p>{revenueScopeText(since)}</p>
      </div>
    </Panel>
  );
}

/* ------------------------------------------------------------------ */
/* Transactions (GET bookings/getAllTransactions)                      */
/* ------------------------------------------------------------------ */

const txItem = (t) => {
  const d = t.userpurchasesDetail?.selectedPassDetail || {};
  const n = d.productName || d.passName || t.bookingsDetail?.className || (t.transactionsFor === "booking" ? "Class booking" : "Purchase");
  return n.charAt(0).toUpperCase() + n.slice(1);
};
const txVia = (t) => {
  const m = t.stripeTransactionDetail?.metadata || {};
  if (m.stripeSubscriptionId && !m.stripeCheckoutSessionsId) return "Subscription payment";
  if (m.stripeinvoiceId && !m.stripeSubscriptionId) return "Invoice";
  if (m.stripeCheckoutSessionsId) return "Online checkout";
  return "Online";
};
const txStatus = (t) => {
  const s = String(t.paymentStatus || "Paid").toLowerCase();
  if (s.includes("refund")) return { label: "Refunded", tone: "gray" };
  if (s === "unpaid" || s === "pending") return { label: "Pending", tone: "amber" };
  return { label: "Paid", tone: "green" };
};

/**
 * Recent transactions. `compact` = Home (5 rows, "View all" link); otherwise a full list with "Show more".
 */
export function Transactions({ compact = false, memberIndex, onViewAll }) {
  const navigate = useNavigate();
  const pageSize = compact ? 5 : 15;
  const [rows, setRows] = React.useState(null);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(1);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [error, setError] = React.useState(false);

  const load = React.useCallback(
    async (p = 1) => {
      p === 1 ? setRows(null) : setLoadingMore(true);
      setError(false);
      try {
        const res = await axiosInstance({ method: "get", url: `${getUrlForBookings()}getAllTransactions?page=${p}&limit=${pageSize}&listFor=class` });
        const data = res?.status === 200 && res?.data?.statusCode === 200 ? res.data.data : null;
        const list = Array.isArray(data?.data) ? data.data : [];
        setRows((prev) => (p === 1 ? list : [...(prev || []), ...list.filter((x) => !(prev || []).some((y) => y._id === x._id))]));
        setTotal(parseInt(data?.totalCount, 10) || list.length);
        setPage(p);
        if (!data) setError(true);
      } catch (e) {
        if (p === 1) setRows([]);
        setError(true);
      } finally {
        setLoadingMore(false);
      }
    },
    [pageSize]
  );
  React.useEffect(() => {
    load(1);
  }, [load]);

  const hasMore = !compact && rows && rows.length < total;
  const receipts = useReceiptActions();

  return (
    <Panel
      title="Recent transactions"
      subtitle={compact ? "Latest payments from your members" : "Every online payment from your members"}
      action={compact ? <LinkButton onClick={onViewAll}>View all ›</LinkButton> : null}
    >
      <div className="mt-3">
        {rows === null ? (
          <ListSkeleton rows={compact ? 4 : 6} />
        ) : rows.length === 0 ? (
          <EmptyLine>{error ? "Couldn't load transactions." : "No payments yet."}</EmptyLine>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[680px] border-collapse">
              <thead>
                <tr className="bg-[#FAF8FB] text-left text-[11px] font-extrabold uppercase tracking-wide text-gray-500">
                  <th className="rounded-l-xl px-3 py-2">Date</th>
                  <th className="px-3 py-2">Member</th>
                  <th className="px-3 py-2">Item</th>
                  <th className="px-3 py-2">Paid via</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                  <th className="w-12 rounded-r-xl px-1 py-2">
                    <span className="sr-only">Receipt</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((t) => {
                  const st = txStatus(t);
                  const student = t.studentDetail || {};
                  const member = memberIndex?.get(String(student._id || t.consumerId || ""));
                  return (
                    <tr key={t._id} className="border-t border-gray-100 text-sm">
                      <td className="whitespace-nowrap px-3 py-2.5 text-gray-500">{moment(t.createdAt).format("D MMM YYYY")}</td>
                      <td className="px-3 py-2.5">
                        <span className="flex min-w-0 items-center gap-2">
                          <Avatar src={student.profilePic} name={student.fullName} size={28} />
                          {member ? (
                            <button type="button" onClick={() => navigate(`/mymembers/detail?id=${member.consumerId}&mid=${member.memberId}`)} className="truncate font-extrabold text-gray-900 hover:text-indigo-bz">
                              {student.fullName || "Member"}
                            </button>
                          ) : (
                            <span className="truncate font-extrabold text-gray-900">{student.fullName || "Member"}</span>
                          )}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-gray-900">{txItem(t)}</td>
                      <td className="whitespace-nowrap px-3 py-2.5 text-gray-500">{txVia(t)}</td>
                      <td className="px-3 py-2.5">
                        <Badge tone={st.tone}>{st.label}</Badge>
                      </td>
                      <td className="whitespace-nowrap px-3 py-2.5 text-right font-black text-gray-900">{euro(t.amount)}</td>
                      <td className="px-1 py-1.5">
                        <ReceiptMenu
                          label={`Receipt for ${student.fullName || "this payment"}`}
                          onView={() => receipts.view({ transactionId: t._id })}
                          onEmail={() => receipts.email({ transactionId: t._id })}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {hasMore && (
          <div className="mt-2 flex justify-center">
            <button type="button" disabled={loadingMore} onClick={() => load(page + 1)} className="flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-extrabold text-indigo-bz hover:bg-[#F3E9FF] disabled:opacity-60">
              {loadingMore && <span className="h-4 w-4 animate-spin rounded-full border-2 border-[#E3D0F7] border-t-indigo-bz" />}
              Show more
            </button>
          </div>
        )}
      </div>
      {receipts.modal}
    </Panel>
  );
}
